import {
  Badge,
  Flex,
  Table,
  TableCaption,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useMemo } from "react";
import {
  BLUEMONEDDBUTTONS,
  GREENMONEDD,
  REDMONEDD,
  WHITE,
} from "../../../../constants/colors";
import { simplifiedExtract } from "../../../../interfaces/defaultInterfaces";
import moment from "moment";
import { ToLocaleFormat } from "../../../../defaultFunctions/formaters";
import PaginacaoTabela, { usePaginaLocal } from "./paginacaoTabela";

// Blocos do EXTRATO SPLIT (tabela ExtratoSplit), um por tipo de lancamento.
//
// A ExtratoSplit e um clone da Extrato e usa os MESMOS ids de tipo, entao as
// vendas splitadas caem aqui tambem. Elas ficam de fora de proposito: a linha da
// venda no extrato principal ja mostra o valor splitado, e repetir a venda aqui
// so duplicaria a informacao. Sobra o que so existe deste lado — o repasse
// (Transferencia TED) e os lancamentos manuais.
//
// Os cards de resumo continuam somando o razao INTEIRO (inclusive vendas), senao
// o saldo do split nao fecharia com entradas - saidas.
//
// Ordem: creditos primeiro, depois debitos; dentro de cada sinal, por id do tipo.
const TIPOS_VISIVEIS = [
  15, // Transferencia TED (repasse automatico do saldo splitado)
  16, // Lancamento Manual Credito
  17, // Lancamento Manual Debito
  // O estorno da remessa que falhou. Anda junto com o tipo 15 e sem ele a lista
  // mente: no dia 28/07 do cliente 1401 sairam 4 TEDs (R$ 120,00) e 3 voltaram
  // (R$ 90,00) — mostrar so as saidas daria a entender que R$ 120,00 deixaram a
  // conta quando foram R$ 30,00. O extrato principal ja exibe este tipo.
  20, // Cancelamento de Remessa Transferencia
];

export default function TableSplitExtract({ data }: any) {
  const grupos = useMemo(() => {
    const linhas: Array<simplifiedExtract> = data ?? [];
    const porTipo = new Map<
      number,
      { lancamento: string; sinal: string; itens: Array<simplifiedExtract> }
    >();

    linhas.forEach((item: any) => {
      const tipo = item?.tipoLancamento;
      if (!tipo) return;
      if (!TIPOS_VISIVEIS.includes(tipo.id)) return;
      const atual = porTipo.get(tipo.id);
      if (atual) {
        atual.itens.push(item);
      } else {
        porTipo.set(tipo.id, {
          lancamento: tipo.lancamento,
          sinal: tipo.sinal,
          itens: [item],
        });
      }
    });

    const peso = (sinal: string) => (sinal === "C" ? 0 : sinal === "D" ? 1 : 2);

    return Array.from(porTipo.entries())
      .map(([id, grupo]) => ({ id, ...grupo }))
      .sort((a, b) => peso(a.sinal) - peso(b.sinal) || a.id - b.id);
  }, [data]);

  if (!grupos.length) return <></>;

  return (
    <Flex w={"100%"} flexDirection={"column"}>
      {grupos.map((grupo) => (
        <BlocoSplit key={grupo.id} grupo={grupo} />
      ))}
    </Flex>
  );
}

interface GrupoSplit {
  id: number;
  lancamento: string;
  sinal: string;
  itens: Array<simplifiedExtract>;
}

// Um bloco por tipo de lancamento. Sai em componente proprio porque a paginacao
// e um hook: dentro do map do componente acima nao poderia ser chamada.
// O total do rodape continua somando o grupo INTEIRO, nao a pagina.
function BlocoSplit({ grupo }: { grupo: GrupoSplit }) {
  const { pagina, setPagina, itens, totalPaginas, total, inicio, tamanho } =
    usePaginaLocal(grupo.itens);

  const credito = grupo.sinal === "C";

  return (
    <Flex w={"100%"} flexDirection={"column"} mt={4}>
      <Flex m={4} alignItems={"center"} gap={2}>
        <Text fontSize={"16px"} fontWeight={"bold"}>
          {grupo.lancamento} — Extrato Split
        </Text>
        <Badge colorScheme={credito ? "green" : "red"}>
          {credito ? "Entrada" : "Saída"}
        </Badge>
      </Flex>
      <Table variant={"striped"}>
        <TableCaption fontWeight={"bold"}>
          <Text as={"span"} color={credito ? GREENMONEDD : REDMONEDD}>
            Total:{" "}
            {ToLocaleFormat(
              grupo.itens.reduce((acc, i) => acc + i.valor, 0)
            )}
          </Text>
        </TableCaption>
        <Thead
          h="60px"
          position="sticky"
          top="0"
          bgColor={BLUEMONEDDBUTTONS}
        >
          <Tr>
            <Th
              borderRadius={"8px 0 0 8px"}
              bgColor={BLUEMONEDDBUTTONS}
              color={WHITE}
            >
              Data
            </Th>
            <Th bgColor={BLUEMONEDDBUTTONS} color={WHITE}>
              Descrição
            </Th>
            <Th
              borderRadius={"0 8px 8px 0"}
              bgColor={BLUEMONEDDBUTTONS}
              color={WHITE}
            >
              Valor
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {itens.map((item: any, index: number) => {
            return (
              <Tr key={index}>
                <Td borderRadius={"8px 0 0 8px"}>
                  {moment(item?.dataLancamento).format(
                    "DD/MM/YYYY HH:mm"
                  )}
                </Td>
                <Td>
                  {item?.observacao ||
                    item?.transacao?.idTransacao ||
                    "—"}
                </Td>
                <Td borderRadius={"0 8px 8px 0"}>
                  {item?.valor.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <PaginacaoTabela
        pagina={pagina}
        totalPaginas={totalPaginas}
        total={total}
        inicio={inicio}
        tamanho={tamanho}
        setPagina={setPagina}
      />
    </Flex>
  );
}
